import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { PolylineEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { EntityObjectUtils } from '@/plugins/cad/module/CADModule/object/entityObject/entityObjectUtils'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

/**
 * 多段线实体的渲染对象管理器，处理凸度圆弧采样及高亮切换。
 */
export class PolylineObjectManager extends BaseObjectManager<PolylineEntity, Line2> {
  // entity的原材质
  private entityOriginalMaterialMap: Map<string, LineMaterial> = new Map()

  protected createObject(entity: PolylineEntity) {
    const geometry = this.createGeometry(entity)
    const material = this.getCachedMaterial(entity)
    const object = createCadLine2Object(geometry, material)
    object.computeLineDistances()
    object.geometry.computeBoundingBox()
    object.geometry.computeBoundingSphere()

    this.entityOriginalMaterialMap.set(entity.id, object.material)

    return object
  }

  protected onUpdateGeometry(entity: PolylineEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    object.geometry.dispose()
    object.geometry = this.createGeometry(entity)
    object.computeLineDistances()
    object.geometry.computeBoundingBox()
    object.geometry.computeBoundingSphere()
  }

  protected onUpdateMaterial(entity: PolylineEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    this.disposeMaterialFromObject(object)
    const material = this.getCachedMaterial(entity)
    object.material = material
    material.needsUpdate = true

    this.entityOriginalMaterialMap.set(entity.id, object.material)
    this.updateEntityHighlight(entity)
  }

  protected disposeObject(object: Line2): void {
    object.geometry?.dispose?.()
    this.entityOriginalMaterialMap.delete(object.userData.entity.id)
  }

  protected handleEntityHighlight(entity: PolylineEntity, mode: HighlightMode): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    if (mode === HighlightMode.None) {
      // 取消高亮
      object.material = this.entityOriginalMaterialMap.get(entity.id)!
      object.material.needsUpdate = true
      object.computeLineDistances()
    } else {
      // 高亮
      const material =
        mode === HighlightMode.Hover
          ? this.materialManager.getCachedHoverMaterial(entity)
          : this.materialManager.getCachedSelectMaterial(entity)
      object.material = material
      object.material.needsUpdate = true
      object.computeLineDistances()
    }
  }

  private createGeometry(entity: PolylineEntity) {
    const points = EntityObjectUtils.getPolylineSamplePoints(entity)
    return DrawUtils.getLineGeometryByPoints(points)
  }
}
